// The shape every list page shares (ZENITH.md §18): a head with the page's
// primary control, a create panel and an edit panel over `SpecForm`, the
// page's one write lane and where its Problem lands, then loading, failure,
// empty, and the table. Orgs, Customers, Sites, Users and Networks repeated
// this by hand and a fix like #180 had to land in each copy (#196).
//
// The frame owns the sameness and nothing else. A page still names its
// fields, its capabilities and its endpoints; the frame never decides who may
// act — every gate stays in the page and calls the same `can`/`canAnywhere`
// (`authz.ts` is a mirror of what to render; the server decides, #96).
import { useState, type ReactNode } from "react";
import type { QueryState } from "../api/hooks";
import { useMutation } from "../api/mutation";
import type { Problem } from "../api/problem";
import { Empty, Info, Loading, PageTitle, ProblemBox, SpecForm, type FieldSpec, type FormValues } from "../ui";

/** What a panel's form is: the fields and the submit. The frame adds the
 * Problem, the busy flag and the cancel, which are the page's and not the
 * form's. */
export interface FormSpec {
  fields: FieldSpec[];
  initial?: FormValues;
  submitLabel: string;
  onSubmit(values: FormValues): void;
  onChange?(values: FormValues): void;
  /** Holds the submit button until the caller says the form is ready — New
   * site's, until a place is chosen or the address is to be typed (#2203). */
  incomplete?: boolean;
  /** What the form edits, named in the question asked before an unsent edit
   * is left behind (UX-011): the site's or the network's name. */
  subject?: string;
}

/** A panel above the table: a heading, and either a form the frame wires or
 * whatever the page renders itself (a form that first has to load what it
 * offers, a notice instead of a form). */
export interface Panel { title: ReactNode; form?: FormSpec; children?: ReactNode }

/** The panel state of a list page: at most one of the create panel and the
 * edit panel is open, opening one closes the other, and every write goes
 * through one mutation hook so one write is in flight per page (#189).
 * `keyOf` names a row, so the edit form remounts when another row is edited
 * rather than keeping the previous row's values. */
export function useListPage<T>(keyOf: (t: T) => string) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<T>();
  const m = useMutation();
  const startCreate = () => { setEditing(undefined); m.clear(); setCreating(true); };
  const startEdit = (t: T) => { setCreating(false); m.clear(); setEditing(t); };
  const close = () => { setCreating(false); setEditing(undefined); };
  /** Runs a write on the page's lane; when it is accepted, closes whichever
   * panel is open and runs `then` — the list's reload, as a rule. */
  const submit = async (call: () => Promise<{ error?: unknown; response: Response }>, then?: () => void): Promise<boolean> => {
    const ok = await m.run(call);
    if (ok) { close(); then?.(); }
    return ok;
  };
  return { creating, editing, m, startCreate, startEdit, close, submit, keyOf, panelOpen: creating || editing !== undefined };
}

export type ListPageState<T> = ReturnType<typeof useListPage<T>>;

/** The frame. `children` is the table, rendered whenever the page says there
 * is something to render; `empty` is what is said when the read answered and
 * there is nothing. */
export function ListPage<T>({ title, sub, info, primary, head, page, query, loadingLabel, skeleton, failure, create, edit, panels, before, trail, titleMenu, empty, children }: {
  title: string;
  /** One line under the title saying what the titled thing is — a page whose
   * title is an object's name rather than a kind (#329). */
  sub?: ReactNode;
  /** What the page is for, behind the ⓘ beside its title (#1890): prose
   * only, and never a lead line under it. */
  info?: ReactNode;
  /** The page's primary control, rendered when the caller may use it and no
   * create panel is open. */
  primary?: { label: string; onClick(): void; disabled?: boolean; title?: string };
  /** Anything else in the head. */
  head?: ReactNode;
  /** The create and edit plumbing, for a page whose panels are those two.
   * A page with panels of its own kind (Devices) renders them through
   * `panels` and shows its own write lane's Problem. */
  page?: ListPageState<T>;
  query: QueryState<unknown>;
  loadingLabel?: string;
  /** What stands where the table will be while the first read is in flight
   * (#1319): the page's `TableSkeleton` — its real header over placeholder
   * rows, busy — so the table is laid out from the first paint and the rows
   * replace the placeholders in place, with nothing above or below it moving.
   * The loading sentence stays, for assistive technology alone. Drawn under
   * the same condition as the sentence — the first load and a re-key, when
   * the frame has no rows at all — and never through a `reload()`, which keeps
   * the previous answer on screen. A page that draws no table (Topology)
   * declares none and keeps the sentence as it is. */
  skeleton?: ReactNode;
  /** How the read's failure is said, where the page has more to say than the
   * Problem — a site with no cell, a cell that did not answer. */
  failure?: (p: Problem) => ReactNode;
  create?: Panel;
  edit?: (t: T) => Panel;
  /** Panels the page has beyond create and edit, rendered after them. */
  panels?: ReactNode;
  /** Between the panels and the body: a notice, a frozen-site explanation. */
  before?: ReactNode;
  /** Above the head: the breadcrumb that is the scope, on a page about one object (#2344). */
  trail?: ReactNode;
  /** Beside the title: the menu of the object the page is about (#2344). */
  titleMenu?: ReactNode;
  /** Shown when the read answered and the page has no rows: one line, why it
   * is empty behind the ⓘ beside it (`info`, prose only), and one `action`
   * (#1900). `children` is the older second line, kept for a page that has
   * not moved to `info`; a link goes in `action`, never in `info`. */
  empty?: { when: boolean; title: string; info?: ReactNode; action?: ReactNode; children?: ReactNode };
  children?: ReactNode;
}) {
  const creating = page?.creating ?? false;
  const editing = page?.editing;
  const form = (f: FormSpec, key?: string) => page && (
    <SpecForm key={key} fields={f.fields} initial={f.initial} submitLabel={f.submitLabel} onSubmit={f.onSubmit} onChange={f.onChange}
      onCancel={page.close} problem={page.m.problem} busy={page.m.busy} incomplete={f.incomplete} subject={f.subject} />
  );
  const editPanel = editing !== undefined && edit ? edit(editing) : undefined;
  const heading = info || titleMenu
    ? <div className="page-title"><PageTitle>{title}</PageTitle>{titleMenu}{info && <Info label={`About ${title}`}>{info}</Info>}</div>
    : <PageTitle>{title}</PageTitle>;
  return (
    <>
      {trail}
      <div className="page-head">
        {sub ? <div>{heading}<div className="page-sub">{sub}</div></div> : heading}
        {primary && !creating && <button className="btn btn-primary" disabled={primary.disabled} title={primary.title} onClick={primary.onClick}>{primary.label}</button>}
        {head}
      </div>
      {creating && create && (
        <div className="panel"><h2>{create.title}</h2>{create.form ? form(create.form) : create.children}</div>
      )}
      {editPanel && editing !== undefined && page && (
        <div className="panel"><h2>{editPanel.title}</h2>{editPanel.form ? form(editPanel.form, page.keyOf(editing)) : editPanel.children}</div>
      )}
      {panels}
      {/* A write's Problem lands in the panel that made it while one is open, and here when none is. */}
      {page && !page.panelOpen && page.m.problem && <ProblemBox problem={page.m.problem} onDismiss={page.m.clear} />}
      {before}
      {query.status === "loading" && !query.data && (skeleton
        ? <><Loading label={loadingLabel} srOnly />{skeleton}</>
        : <Loading label={loadingLabel} />)}
      {query.status === "error" && (failure ? failure(query.problem) : <ProblemBox problem={query.problem} />)}
      {empty?.when && query.data !== undefined && <Empty title={empty.title} info={empty.info} action={empty.action}>{empty.children}</Empty>}
      {children}
    </>
  );
}
